import { useCallback, useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { AlertTriangle, ArrowLeft, Pencil, SquarePen, Undo2 } from 'lucide-react'
import { toast } from 'sonner'
import { useUserRole } from '@/hooks/useUserRole'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty'
import { Field, FieldLabel } from '@/components/ui/field'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { platformApi } from '../api'
import { fieldIndex } from '../lib/form'
import { AnswersView } from '../components/AnswersView'
import { SeverityBadge, StatusBadge, decisionLabels } from '../components/badges'
import { EditAnswersSheet } from '../components/EditAnswersSheet'
import { AiSummaryCard } from '../components/AiSummaryCard'
import { ChangeWorkflowButton, CommentsTab, ConditionsPanel, RevokeButton } from '../components/ReviewActions'
import { WorkflowTimeline } from '../components/WorkflowTimeline'
import type { AuditEvent, DecisionKind, SubmissionDetail } from '../types'

const severityRank = { high: 0, medium: 1, low: 2, info: 3 } as const

function DecisionPanel({ detail, onDone }: { detail: SubmissionDetail; onDone: () => void }) {
    const { userId } = useUserRole()
    const [comment, setComment] = useState('')
    const [busy, setBusy] = useState<DecisionKind | null>(null)

    const step = detail.steps.find((s) => s.status === 'active')
    if (!step || detail.submission.status !== 'in_review') return null
    const mine = step.assignees.some((a) => a.id === userId)
    const decided = detail.decisions.some((d) => d.step === step.step && d.round === step.round && d.decided_by === userId)
    if (!mine || decided) return null

    const options = detail.workflow?.decisions ?? []
    const needsComment = detail.workflow?.requireAttestation
    // What each choice needs before its button works, so it is clear before pressing it.
    const text = comment.trim()
    const missing = (d: DecisionKind): string => {
        if (d === 'send_back') return text ? '' : 'Write what the submitter needs to change first'
        if (d === 'approve_with_conditions') return text ? '' : 'Write the conditions first'
        if (needsComment && d !== 'acknowledge' && !text) return 'This workflow requires a comment'
        return ''
    }

    const decide = async (d: DecisionKind) => {
        if (needsComment && d !== 'acknowledge' && !comment.trim()) {
            toast.error('Add a comment to record this decision.')
            return
        }
        if (d === 'approve_with_conditions' && !comment.trim()) {
            toast.error('State the conditions in the comment. The requester must accept them.')
            return
        }
        if (d === 'send_back' && !comment.trim()) {
            toast.error('Tell the submitter what needs to change.')
            return
        }
        setBusy(d)
        try {
            if (d === 'send_back') await platformApi.sendBack(detail.submission.id, step.step, comment.trim())
            else await platformApi.decide(detail.submission.id, step.step, d, comment.trim())
            toast.success(`Recorded: ${decisionLabels[d]}`)
            setComment('')
            onDone()
        } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Could not record the decision')
        } finally {
            setBusy(null)
        }
    }

    return (
        <Card className="border-primary/40">
            <CardHeader>
                <CardTitle className="text-base">Your decision</CardTitle>
                <CardDescription>You are a reviewer for “{step.name}”.</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
                <Field>
                    <FieldLabel htmlFor="decision-comment">Comment {needsComment ? <span className="font-normal text-muted-foreground">(required)</span> : <span className="font-normal text-muted-foreground">(optional, except to send back or add conditions)</span>}</FieldLabel>
                    <Textarea id="decision-comment" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} />
                </Field>
                <div className="flex flex-wrap gap-2">
                    {options.map((d) => (
                        <Button key={d} variant={d === 'refuse' ? 'destructive' : d === 'accept' ? 'default' : 'outline'}
                            disabled={busy !== null || missing(d) !== ''} title={missing(d) || undefined} onClick={() => decide(d)}>
                            {busy === d && <Spinner data-icon="inline-start" />}{decisionLabels[d]}
                        </Button>
                    ))}
                    <Button variant="outline" disabled={busy !== null || missing('send_back') !== ''} title={missing('send_back') || undefined} onClick={() => decide('send_back')}>
                        {busy === 'send_back' ? <Spinner data-icon="inline-start" /> : <Undo2 data-icon="inline-start" />}Send back to submitter
                    </Button>
                </div>
            </CardContent>
        </Card>
    )
}

function History({ events, definition }: { events: AuditEvent[] | null; definition: SubmissionDetail['definition'] }) {
    if (events === null) return <Skeleton className="h-24" />
    const fields = fieldIndex(definition)
    return (
        <ol className="flex flex-col gap-3">
            {[...events].reverse().map((e) => {
                const ch = e.changes as { answers?: { added?: string[]; changed?: string[]; removed?: string[] }; step?: number; decision?: string; comment?: string }
                const keys = [...(ch.answers?.added ?? []), ...(ch.answers?.changed ?? []), ...(ch.answers?.removed ?? [])]
                return (
                    <li key={e.id} className="rounded-lg border bg-card px-4 py-3 text-sm">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium">{e.action === 'reviewer_edit' ? 'Edited by reviewer' : e.action === 'revoke' ? 'Approval revoked' : e.action === 'workflow_changed' ? 'Workflow changed' : e.action.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())}</span>
                            <span className="text-muted-foreground">by {e.actor_name || e.actor_id}</span>
                            <span className="ml-auto text-xs text-muted-foreground">{new Date(e.created_at).toLocaleString()}</span>
                        </div>
                        {e.action === 'decision' && ch.decision && (
                            <p className="mt-1 text-muted-foreground">Step {ch.step}: {ch.decision.replace(/_/g, ' ')}{ch.comment ? ` — “${ch.comment}”` : ''}</p>
                        )}
                        {['send_back', 'comment', 'conditions_accepted', 'conditions_declined'].includes(e.action) && ch.comment && (
                            <p className="mt-1 text-muted-foreground">{e.action === 'send_back' ? `Step ${ch.step}: ` : ''}“{ch.comment}”</p>
                        )}
                        {e.action === 'workflow_changed' && (
                            <p className="mt-1 text-muted-foreground">
                                {String((e.changes as { from_workflow?: string }).from_workflow || 'No workflow')} → {String((e.changes as { to_workflow?: string }).to_workflow)}
                            </p>
                        )}
                        {['reviewer_edit', 'revoke', 'workflow_changed'].includes(e.action) && typeof (e.changes as { reason?: string }).reason === 'string' && (
                            <p className="mt-1 text-muted-foreground">Reason: “{(e.changes as { reason?: string }).reason}”</p>
                        )}
                        {keys.length > 0 && <p className="mt-1 text-xs text-muted-foreground">Changed: {keys.map((k) => fields[k]?.label ?? k).join('; ')}</p>}
                    </li>
                )
            })}
        </ol>
    )
}

export default function SubmissionPage({ submissionId }: { submissionId: string }) {
    const { userId, isRiskManager } = useUserRole()
    const [detail, setDetail] = useState<SubmissionDetail | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [audit, setAudit] = useState<AuditEvent[] | null>(null)
    const [editing, setEditing] = useState(false)

    const load = useCallback(() => {
        platformApi.getSubmission(submissionId).then((d) => { setDetail(d); setError(null) })
            .catch((e) => setError(e instanceof Error ? e.message : 'Could not load'))
        platformApi.submissionAudit(submissionId).then(setAudit).catch(() => setAudit([]))
    }, [submissionId])
    useEffect(load, [load])

    if (error) {
        return (
            <div className="mx-auto max-w-xl py-8">
                <Alert variant="destructive"><AlertTriangle /><AlertTitle>Cannot open this submission</AlertTitle>
                    <AlertDescription>{error}</AlertDescription></Alert>
            </div>
        )
    }
    if (!detail) {
        return <div className="flex flex-col gap-4"><Skeleton className="h-10 w-80" /><Skeleton className="h-64" /></div>
    }

    const s = detail.submission
    const findings = [...detail.findings].sort((a, b) => severityRank[a.severity] - severityRank[b.severity])
    const mine = s.submitter_id === userId

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3">
                <Button asChild variant="ghost" size="sm" className="w-fit -ml-2 text-muted-foreground">
                    <Link to="/submissions"><ArrowLeft data-icon="inline-start" /> Submissions</Link>
                </Button>
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex flex-col gap-1">
                        <h1 className="text-2xl font-semibold tracking-tight">{s.form_name}</h1>
                        <p className="text-sm text-muted-foreground">
                            <span className="font-mono">{s.number}</span> · {s.submitter_name}
                            {s.submitted_at && ` · submitted ${new Date(s.submitted_at).toLocaleDateString()}`}
                            {isRiskManager && ` · v${s.form_version}`}
                        </p>
                        {s.title && <p className="text-sm">{s.title}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                        <StatusBadge status={s.status} />
                        {(s.status === 'draft' || s.status === 'returned') && mine && (
                            <Button asChild size="sm"><Link to="/submissions/$id/edit" params={{ id: s.id }}><Pencil data-icon="inline-start" />{s.status === 'returned' ? 'Update and resubmit' : 'Continue'}</Link></Button>
                        )}
                        {isRiskManager && <ChangeWorkflowButton detail={detail} onDone={load} />}
                        {isRiskManager && <RevokeButton detail={detail} onDone={load} />}
                    </div>
                </div>
            </div>

            {s.status === 'returned' && (() => {
                const back = [...detail.decisions].reverse().find((d) => d.decision === 'send_back')
                return (
                    <Alert className="border-orange-500/50 bg-orange-500/5">
                        <Undo2 />
                        <AlertTitle>{mine ? 'This request was sent back to you' : 'Sent back to the submitter'}</AlertTitle>
                        <AlertDescription>
                            {back ? `${back.decided_by_name}: “${back.comment}”` : 'Waiting for the submitter to update and resubmit.'}
                        </AlertDescription>
                    </Alert>
                )
            })()}

            {detail.can_edit && (
                <EditAnswersSheet open={editing} onOpenChange={setEditing} submissionId={s.id}
                    definition={detail.definition} answers={s.answers} onSaved={load} />
            )}

            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_24rem] lg:items-start xl:grid-cols-[minmax(0,1fr)_28rem]">
                {/* Decision and progress sit beside the content on wide screens, and first on small ones. */}
                <aside className="order-first flex flex-col gap-6 lg:sticky lg:top-20 lg:order-last">
                    <ConditionsPanel detail={detail} onDone={load} />
                    <DecisionPanel detail={detail} onDone={load} />
                    {detail.steps.length > 0 && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base">Approval{detail.workflow ? ` · ${detail.workflow.name}` : ''}</CardTitle>
                            </CardHeader>
                            <CardContent><WorkflowTimeline steps={detail.steps} decisions={detail.decisions} orientation="vertical" /></CardContent>
                        </Card>
                    )}
                </aside>

                <div className="flex min-w-0 flex-col gap-6">
                    {detail.ai?.available && (
                        <AiSummaryCard submissionId={s.id} definition={detail.definition} initial={detail.ai} />
                    )}

            <Tabs defaultValue="answers">
                <TabsList>
                    <TabsTrigger value="answers">Answers</TabsTrigger>
                    <TabsTrigger value="findings">Findings{findings.length > 0 && ` (${findings.length})`}</TabsTrigger>
                    <TabsTrigger value="comments">Comments{(detail.comments?.length ?? 0) > 0 && ` (${detail.comments?.length})`}</TabsTrigger>
                    <TabsTrigger value="history">History</TabsTrigger>
                </TabsList>
                <TabsContent value="answers" className="pt-4">
                    {detail.can_edit && (
                        <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border bg-muted/40 px-4 py-2.5 text-sm">
                            <span className="text-muted-foreground">You can correct these answers while you review. Changes are recorded in the history.</span>
                            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                                <SquarePen data-icon="inline-start" />Edit answers
                            </Button>
                        </div>
                    )}
                    <AnswersView definition={detail.definition} answers={s.answers} files={detail.files ?? []}
                        submissionId={s.id} fileAccess={detail.file_access ?? 'none'} />
                </TabsContent>
                <TabsContent value="findings" className="pt-4">
                    {findings.length === 0 ? (
                        <Empty><EmptyHeader><EmptyTitle>No findings</EmptyTitle>
                            <EmptyDescription>None of this form's rules were triggered by the answers.</EmptyDescription></EmptyHeader></Empty>
                    ) : (
                        <ul className="flex flex-col gap-2">
                            {findings.map((f) => (
                                <li key={f.id} className="flex flex-col gap-1.5 rounded-lg border bg-card px-4 py-3 text-sm">
                                    <div className="flex items-center gap-2"><SeverityBadge severity={f.severity} />
                                        {f.section && <span className="text-xs text-muted-foreground">{f.section}</span>}</div>
                                    <p>{f.message}</p>
                                    {f.triggered_by_answer && <p className="text-xs text-muted-foreground">Triggered by answer: {f.triggered_by_answer}</p>}
                                </li>
                            ))}
                        </ul>
                    )}
                </TabsContent>
                <TabsContent value="comments" className="pt-4"><CommentsTab detail={detail} isRequester={mine} onDone={load} /></TabsContent>
                <TabsContent value="history" className="pt-4"><History events={audit} definition={detail.definition} /></TabsContent>
            </Tabs>
                </div>
            </div>
        </div>
    )
}
